import { motion } from "framer-motion";
import { toast } from "sonner";
import { StepShell } from "./Section";
import { Sparkles, Zap, Compass, Heart, ListChecks, RotateCcw, Share2, Printer } from "lucide-react";

const Block = ({ icon: Icon, title, color, delay, children }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.55, delay }}
    className="bg-white/85 backdrop-blur-sm rounded-2xl p-6 sm:p-7 border border-[#D4AF37]/25 shadow-sm"
  >
    <div className="flex items-center gap-2.5 mb-3">
      <span className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-[#FFF6DC]" style={{ color }}>
        <Icon size={20} strokeWidth={1.9} />
      </span>
      <h3 className="serif text-xl font-semibold" style={{ color }}>{title}</h3>
    </div>
    {children}
  </motion.div>
);

export const ReadingView = ({ result, cards, positions, meta, onRestart, restartLabel = "Neue Analyse" }) => {
  const shareUrl = `${window.location.origin}/r/${result.id}`;

  const handleShare = async () => {
    const data = {
      title: "Berufs- & Seelenweg-Analyse",
      text: "Meine persönliche Berufs- & Seelenweg-Analyse",
      url: shareUrl,
    };
    try {
      if (navigator.share) {
        await navigator.share(data);
      } else {
        await navigator.clipboard.writeText(shareUrl);
        toast.success("Link kopiert – jetzt teilen!");
      }
    } catch (e) {
      try {
        await navigator.clipboard.writeText(shareUrl);
        toast.success("Link kopiert – jetzt teilen!");
      } catch (_) {
        toast.error("Teilen nicht möglich.");
      }
    }
  };

  return (
    <StepShell>
      <div className="text-center mb-10">
        <span className="serif inline-block text-sm tracking-[0.25em] uppercase text-[#b8860b] mb-2">
          {meta.topicLabel} · {meta.spreadLabel}
        </span>
        <h2 className="script-title text-4xl sm:text-5xl">Deine Analyse</h2>
      </div>

      {/* Drawn cards strip */}
      <div className="flex flex-wrap justify-center gap-4 mb-10">
        {cards.map((c, i) => (
          <div key={c.id} className="flex flex-col items-center w-[92px]">
            <img src={c.img} alt={c.name} className="w-full aspect-[2/3] object-cover rounded-lg glow-soft" />
            <span className="serif mt-2 text-[15px] font-semibold text-[#b8860b] text-center leading-tight">{positions[i]}</span>
            <span className="text-[14px] text-[#1E3A8A] text-center">{c.name}</span>
          </div>
        ))}
      </div>

      <div className="space-y-5">
        {/* Card meanings */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.55 }}
          className="bg-[#FDFBF7] rounded-2xl p-6 sm:p-7 border border-[#D4AF37]/30"
        >
          <h3 className="serif text-xl font-semibold text-[#4C1D95] mb-4">Die Karten im Detail</h3>
          <div className="space-y-4">
            {result.cards.map((cm) => (
              <div key={`${cm.position}-${cm.name}`} className="border-l-2 border-[#D4AF37]/50 pl-4">
                <h4 className="text-[16px] font-semibold text-[#1E3A8A]">
                  {cm.position}: <span className="text-[#b8860b]">{cm.name}</span>
                </h4>
                <p className="mt-1 text-[15px] leading-[1.6] text-[#222]">{cm.meaning}</p>
              </div>
            ))}
          </div>
        </motion.div>

        <Block icon={Sparkles} title="Zusammenfassung" color="#4C1D95" delay={0.05}>
          <p className="text-[15px] leading-[1.6] text-[#222]">{result.summary}</p>
        </Block>

        <Block icon={Zap} title="Energieimpuls" color="#B8860B" delay={0.1}>
          <p className="text-[15px] leading-[1.6] text-[#222]">{result.energyImpulse}</p>
        </Block>

        <Block icon={Compass} title="Empfehlung" color="#1E3A8A" delay={0.15}>
          <p className="text-[15px] leading-[1.6] text-[#222]">{result.recommendation}</p>
        </Block>

        <Block icon={Heart} title="Seelenbotschaft" color="#9333EA" delay={0.2}>
          <p className="text-[15px] leading-[1.6] text-[#222] italic">{result.soulMessage}</p>
        </Block>

        <Block icon={ListChecks} title="3 klare Schritte" color="#B8860B" delay={0.25}>
          <ol className="space-y-3">
            {result.steps.map((step, i) => (
              <li key={step} className="flex gap-3 text-[15px] leading-[1.6] text-[#222]">
                <span className="shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-full bg-[#D4AF37] text-white text-[15px] font-semibold">
                  {i + 1}
                </span>
                <span className="pt-0.5">{step}</span>
              </li>
            ))}
          </ol>
        </Block>
      </div>

      <div className="no-print flex flex-wrap items-center justify-center gap-3 mt-10">
        <button
          data-testid="share-btn"
          onClick={handleShare}
          className="serif text-lg inline-flex items-center gap-2 px-8 py-3 rounded-full bg-gradient-to-r from-[#F0C548] to-[#B8860B] text-white glow-soft hover:glow-strong transition-all duration-300"
        >
          <Share2 size={18} /> Analyse teilen
        </button>
        <button
          data-testid="print-btn"
          onClick={() => window.print()}
          className="serif text-lg inline-flex items-center gap-2 px-8 py-3 rounded-full border border-[#D4AF37] text-[#b8860b] hover:bg-white hover:glow-soft transition-all duration-300"
        >
          <Printer size={18} /> Drucken / PDF
        </button>
        <button
          data-testid="restart-btn"
          onClick={onRestart}
          className="serif text-lg inline-flex items-center gap-2 px-8 py-3 rounded-full border border-[#4C1D95]/30 text-[#4C1D95] hover:bg-white hover:glow-soft transition-all duration-300"
        >
          <RotateCcw size={18} /> {restartLabel}
        </button>
      </div>
    </StepShell>
  );
};
